import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, onUnmounted, computed, watch, ComputedRef } from 'vue';
import orderBy from 'lodash/orderBy';

import { UserIcon } from '@heroicons/vue/solid';
import { PauseIcon, PlayIcon } from '@heroicons/vue/outline';

import { useFilterStore } from '@/stores';
import { RealtimeReportService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import {
    RealtimeReportServiceGetUserDevicesResult,
    UserDeviceType,
    UserDeviceStatus,
    UserModel,
    UserDeviceStateModel,
    AgentExtensionReportGetters,
    AgentExtensionReportActions,
    AgentExtensionReportFilter,
    SortMenuItem,
    SortMenuItemDirection,
    Permission,
    SelectOptionsServiceGetUserLabelsResult,
    FilterItem,
} from '@/types';
import {
    AgentExtensionReportFilters,
    AgentExtensionReportSort,
} from '@/views/pages/reports/agent-extension/components';
import store from '@/store';
import { useI18n } from 'vue-i18n';

type UserDeviceTable = Partial<
    Record<
        UserDeviceType,
        {
            state: UserDeviceStateModel | null;
            status: UserDeviceStatus;
        }
    >
>;

interface UserTableRow {
    user: RealtimeReportUser;
    devices: UserDeviceTable;
}

interface RealtimeReportUser extends UserModel {
    labels?: SelectOptionsServiceGetUserLabelsResult[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentExtensionReportPage',
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();

// global filter state
const filterStore = useFilterStore();
const levelFilter = computed(() => filterStore.level);
const filter = computed<AgentExtensionReportFilter>(
    () => store.getters[AgentExtensionReportGetters.FILTER],
);
const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[AgentExtensionReportGetters.ACTIVE_FILTERS],
);

const search = computed<string>({
    get: () => store.getters[AgentExtensionReportGetters.SEARCH],
    set: (value) => store.dispatch(AgentExtensionReportActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[AgentExtensionReportGetters.SORT],
    set: (value) => store.dispatch(AgentExtensionReportActions.SET_SORT, value),
});

// Reactive state
const userDevices = ref<RealtimeReportServiceGetUserDevicesResult[]>([]);
const isLoading = ref(false);
const isRunning = ref(false);

const tableHeaders = [
    {
        key: 'name',
        label: 'report.realtimeAgentExtension.details.userName',
        align: 'left',
    },
    {
        key: 'mobile',
        label: 'report.realtimeAgentExtension.details.mobile',
        align: 'center',
    },
    {
        key: 'desktop',
        label: 'report.realtimeAgentExtension.details.desktop',
        align: 'center',
    },
    {
        key: 'web',
        label: 'report.realtimeAgentExtension.details.web',
        align: 'center',
    },
    {
        key: 'external',
        label: 'report.realtimeAgentExtension.details.external',
        align: 'center',
    },
    {
        key: 'queue',
        label: 'report.realtimeAgentExtension.details.queues',
        align: 'center',
    },
];

const deviceTypes = [
    UserDeviceType.MOBILE,
    UserDeviceType.DESKTOP,
    UserDeviceType.WEB,
    UserDeviceType.EXTERNAL,
];

let pollingTimeoutId: number | null = null;

const connectedUserDeviceStatuses = new Set([
    UserDeviceStatus.CONNECTED,
    UserDeviceStatus.DIALING,
    UserDeviceStatus.RING,
    UserDeviceStatus.RINGING,
    UserDeviceStatus.INUSE,
    UserDeviceStatus.IN_CALL,
]);

const computedUserTableRows = computed<UserTableRow[]>(() => {
    const users = new Map<number, UserTableRow>();

    for (const userDevice of userDevices.value) {
        const extensionNumber = userDevice.user.extension!.number;
        let user = users.get(extensionNumber);

        if (!user) {
            user = {
                user: userDevice.user,
                devices: {},
            };

            users.set(extensionNumber, user);
        }

        const status = userDevice.user.queueStatusHistory?.length
            ? UserDeviceStatus.IN_PAUSE
            : (userDevice.state?.status as UserDeviceStatus | undefined) ||
              UserDeviceStatus.OFFLINE;

        user.devices[userDevice.deviceType as UserDeviceType] = {
            state: userDevice.state,
            status,
        };
    }

    let rows = Array.from(users.values());

    if (filter.value.status) {
        rows = rows.filter((row) =>
            Object.values(row.devices).some(
                (device) => device?.status === filter.value.status?.value,
            ),
        );
    }

    if (filter.value.user) {
        rows = rows.filter((row) => row.user.extension?.number === filter.value.user?.id);
    }

    if (filter.value.userLabel) {
        rows = rows.filter((row) =>
            row.user.labels?.some((label) => label.id === filter.value.userLabel?.id),
        );
    }

    if (filter.value.queue) {
        rows = rows.filter((row) =>
            row.user.queues?.some((queue) => queue.id === filter.value.queue?.id),
        );
    }

    if (search.value) {
        const searchValue = search.value.toLowerCase().trim();

        rows = rows.filter(
            (row) =>
                row.user.name.toLowerCase().includes(searchValue) ||
                row.user.extension?.number?.toString().includes(searchValue),
        );
    }

    const sortFields: Record<string, (row: UserTableRow) => string | number> = {
        name: (row) => row.user.name.toLowerCase(),
        extension: (row) => row.user.extension!.number,
    };

    const selectedSorts = sortMenuSelected.value;

    if (selectedSorts.length) {
        return orderBy(
            rows,
            selectedSorts.map((sort) => sortFields[sort.key]),
            selectedSorts.map((sort) =>
                sort.direction === SortMenuItemDirection.ASC ? 'asc' : 'desc',
            ),
        );
    }

    return orderBy(rows, [sortFields.name, sortFields.extension], ['asc', 'asc']);
});

// Called periodically to update real-time data
const fetchData = async () => {
    try {
        isLoading.value = true;
        const usersResp = await RealtimeReportService.getUserDevices();
        userDevices.value = usersResp.data;
    } catch (err) {
        toastServiceError(err);
    } finally {
        isLoading.value = false;
    }
};

// Starts polling every 5s — restarts automatically if still running
const startPolling = async () => {
    isRunning.value = true;
    const loop = async () => {
        if (!isRunning.value) return;
        await fetchData();
        if (isRunning.value) pollingTimeoutId = window.setTimeout(loop, 5000);
    };
    loop();
};

// Stops polling on unmount or when filters change
const stopPolling = () => {
    isRunning.value = false;
    if (pollingTimeoutId) clearTimeout(pollingTimeoutId);
};

// Lifecycle: start/stop polling
onMounted(() => startPolling());
onUnmounted(() => stopPolling());

const toggleQueueMemberPause = async (userId: number) => {
    try {
        const response = await RealtimeReportService.toggleQueueMemberPause(userId);
        toast.success(response.data.message);
        await fetchData();
    } catch (error) {
        toastServiceError(error);
    }
};

const handleRemoveFilter = (filter: FilterItem) => {
    store.dispatch(AgentExtensionReportActions.CLEAR_FILTER, filter.field);
};

// Restart polling on level change — ensures data stays scoped to current filter
watch([levelFilter], () => {
    stopPolling();
    startPolling();
});

const __returned__ = { t, filterStore, levelFilter, filter, activeFilters, search, sortMenuSelected, userDevices, isLoading, isRunning, tableHeaders, deviceTypes, get pollingTimeoutId() { return pollingTimeoutId }, set pollingTimeoutId(v) { pollingTimeoutId = v }, connectedUserDeviceStatuses, computedUserTableRows, fetchData, startPolling, stopPolling, toggleQueueMemberPause, handleRemoveFilter, get UserIcon() { return UserIcon }, get PauseIcon() { return PauseIcon }, get PlayIcon() { return PlayIcon }, get UserDeviceStatus() { return UserDeviceStatus }, get Permission() { return Permission }, get AgentExtensionReportFilters() { return AgentExtensionReportFilters }, get AgentExtensionReportSort() { return AgentExtensionReportSort } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})